import React from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { accountingNumberFormatter } from "@common/helpers/number";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { TransferMatchCellContent } from "@components/transactions/views/TransferMatchCell";
import transferMatchStyles from "@components/transactions/views/TransferMatchCell.module.scss";
import { MatchedIcon, UnmatchedIcon } from "@icons";
import { useEntitiesById } from "@hooks/useEntities";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { IntercompanyComputedMatchPreview } from "./IntercompanyComputedMatchPreview";
import {
    getComputedMatchContext,
    getIntercompanyMatchPresentation,
} from "./intercompanyMatchPresentation";
import {
    IntercompanyTransactionsTableCallbacks,
    IntercompanyTransactionsViewOptions,
} from "./intercompanyTransactionsViewConfig";
import generalLedgerStyles from "../generalLedger/GeneralLedgerTable.module.scss";
import { IntercompanyTransactionRow } from "./intercompanyTransactions.lib";
import styles from "./IntercompanyTransactionsTable.module.scss";

interface IntercompanyTransactionsCellContext {
    row: { original: IntercompanyTransactionRow };
    table: { options: { meta?: object } };
}

type StatusIcon = React.FC<React.SVGProps<SVGSVGElement>>;

function renderIntercompanyText(value: string): React.ReactNode {
    return value.trim() === "" ? <EmptyValue /> : value;
}

function getIntercompanyStatusPresentation(
    status: IntercompanyTransactionRow["status"],
): { className: string; icon: StatusIcon } {
    switch (status) {
        case "Matched":
            return { className: styles.matched, icon: MatchedIcon };
        case "Unmatched":
            return { className: styles.unmatched, icon: UnmatchedIcon };
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

export const IntercompanyDateCell = React.memo(function IntercompanyDateCell({
    row,
}: IntercompanyTransactionsCellContext): React.ReactElement {
    return (
        <ViewTableCell>
            {row.original.date ? (
                format(parseISO(row.original.date), "MM/dd/yyyy")
            ) : (
                <EmptyValue />
            )}
        </ViewTableCell>
    );
});

export const IntercompanyEntityCell = React.memo(
    function IntercompanyEntityCell({
        row,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        const entity = useEntitiesById().get(row.original.entityId);
        return (
            <ViewTableCell className={styles.entityCell}>
                {entity ? (
                    <AvatarTooltip content={entity.name}>
                        <EntityAvatar
                            size="xs"
                            entity={entity}
                            copyId={entity.id}
                        />
                    </AvatarTooltip>
                ) : (
                    <Tooltip content={row.original.entityName}>
                        <span
                            className={styles.entityFallback}
                            aria-label={row.original.entityName}
                        />
                    </Tooltip>
                )}
            </ViewTableCell>
        );
    },
);

export const IntercompanySourceCell = React.memo(
    function IntercompanySourceCell({
        row,
        table,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        const { callbacks } = useViewTableMeta<
            IntercompanyTransactionsTableCallbacks,
            IntercompanyTransactionsViewOptions
        >(table);
        const isClickable =
            row.original.source === "transaction" &&
            row.original.sourceTransactionId !== undefined &&
            callbacks?.onOpenTransaction !== undefined;

        return (
            <ViewTableCell
                captureClicks={isClickable}
                className={classNames({
                    [generalLedgerStyles.clickableCell]: isClickable,
                })}
                onClick={
                    isClickable
                        ? () => callbacks.onOpenTransaction(row.original)
                        : undefined
                }
            >
                {row.original.sourceLabel}
            </ViewTableCell>
        );
    },
);

export const IntercompanyReferenceCell = React.memo(
    function IntercompanyReferenceCell({
        row,
        table,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        const { callbacks } = useViewTableMeta<
            IntercompanyTransactionsTableCallbacks,
            IntercompanyTransactionsViewOptions
        >(table);
        const { source, journalEntries } = row.original;
        const canOpenJournalEntry =
            journalEntries.length === 1 &&
            callbacks?.onOpenJournalEntry !== undefined;
        const reference =
            journalEntries[0] &&
            (source === "manual_je" || journalEntries.length === 1)
                ? getShortJournalEntryReferenceNumber(
                      journalEntries[0].referenceNumber,
                  )
                : "";

        return (
            <ViewTableCell captureClicks={canOpenJournalEntry}>
                {canOpenJournalEntry ? (
                    <button
                        type="button"
                        className={styles.referenceButton}
                        onClick={() =>
                            callbacks.onOpenJournalEntry(row.original)
                        }
                    >
                        {renderIntercompanyText(reference)}
                    </button>
                ) : (
                    renderIntercompanyText(reference)
                )}
            </ViewTableCell>
        );
    },
);

export const IntercompanyAccountCell = React.memo(
    function IntercompanyAccountCell({
        row,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        return (
            <ViewTableCell>
                {renderIntercompanyText(row.original.accountLabel)}
            </ViewTableCell>
        );
    },
);

export const IntercompanyDescriptionCell = React.memo(
    function IntercompanyDescriptionCell({
        row,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        return (
            <ViewTableCell>
                {renderIntercompanyText(row.original.description)}
            </ViewTableCell>
        );
    },
);

export const IntercompanyMemoCell = React.memo(function IntercompanyMemoCell({
    row,
}: IntercompanyTransactionsCellContext): React.ReactElement {
    return (
        <ViewTableCell>
            {renderIntercompanyText(row.original.memo)}
        </ViewTableCell>
    );
});

export const IntercompanyAmountCell = React.memo(
    function IntercompanyAmountCell({
        row,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        return (
            <ViewTableCell className={styles.numericCell}>
                {accountingNumberFormatter(row.original.amount)}
            </ViewTableCell>
        );
    },
);

export const IntercompanyStatusCell = React.memo(
    function IntercompanyStatusCell({
        row,
    }: IntercompanyTransactionsCellContext): React.ReactElement {
        const { status } = row.original;
        const { className, icon: StatusIcon } =
            getIntercompanyStatusPresentation(status);

        return (
            <ViewTableCell>
                <span
                    className={classNames(styles.statusCell, className)}
                    aria-label={status}
                >
                    <Tooltip content={status}>
                        <span>
                            <StatusIcon
                                className={classNames(
                                    styles.statusIcon,
                                    "icon-color-current",
                                )}
                                aria-hidden
                            />
                        </span>
                    </Tooltip>
                </span>
            </ViewTableCell>
        );
    },
);

export const IntercompanyMatchCell = React.memo(function IntercompanyMatchCell({
    row,
    table,
}: IntercompanyTransactionsCellContext): React.ReactElement {
    const { callbacks } = useViewTableMeta<
        IntercompanyTransactionsTableCallbacks,
        IntercompanyTransactionsViewOptions
    >(table);
    const intercompanyRow = row.original;
    const { transaction } = intercompanyRow;
    const isDateLocked = useIsDateLocked(
        transaction?.entity.id ?? null,
        intercompanyRow.date,
    );
    const presentation = getIntercompanyMatchPresentation(intercompanyRow);

    switch (presentation.kind) {
        case "in_transit":
            return (
                <ViewTableCell className={styles.mutedCell}>
                    {presentation.counterpartName}
                </ViewTableCell>
            );
        case "transfer_match":
            return transaction ? (
                <TransferMatchCellContent
                    transaction={transaction}
                    isReadonlyOrLocked={isDateLocked}
                    isDateLocked={isDateLocked}
                />
            ) : (
                <ViewTableCell />
            );
        case "computed_match":
            return (
                <ViewTableCell
                    captureClicks
                    className={transferMatchStyles.cell}
                >
                    <EditableCellContent
                        className={classNames(
                            transferMatchStyles.pairedTransfer,
                            styles.matchPreviewButton,
                        )}
                        onClick={() => callbacks?.onOpenMatch(intercompanyRow)}
                        isEmpty={false}
                        aria-label="Open matched journal entry"
                    >
                        <IntercompanyComputedMatchPreview
                            matchContext={getComputedMatchContext(
                                intercompanyRow,
                            )}
                        />
                    </EditableCellContent>
                </ViewTableCell>
            );
        case "add_transaction_match":
            return (
                <ViewTableCell
                    captureClicks
                    className={transferMatchStyles.cell}
                >
                    <EditableCellContent
                        isEmpty
                        onClick={() =>
                            callbacks?.onFindTransactionMatch(intercompanyRow)
                        }
                    />
                </ViewTableCell>
            );
        case "add_journal_entry":
            return (
                <ViewTableCell
                    captureClicks
                    className={transferMatchStyles.cell}
                >
                    <EditableCellContent
                        isEmpty
                        onClick={() =>
                            callbacks?.onCreateJournalEntry(intercompanyRow)
                        }
                    />
                </ViewTableCell>
            );
        default: {
            const exhaustive: never = presentation;
            return exhaustive;
        }
    }
});
